import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldAlert } from "lucide-react";
import { adminApi, setAdminToken, getAdminToken } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";

export default function AdminLogin() {
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (getAdminToken()) return <Navigate to="/admin/overview" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const { data } = await adminApi.post("/admin/login", { email, password });
            setAdminToken(data.token);
            nav("/admin/overview");
        } catch (err) {
            setError(err?.response?.data?.detail || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0B0B0F] text-white grid place-items-center px-4">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-sm"
            >
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#E85D5D] mb-2">
                    <ShieldAlert className="h-3 w-3" strokeWidth={3} /> Flatch · ops console
                </div>
                <h1 className="font-display text-4xl font-black tracking-tight leading-none">
                    Admin sign-in
                </h1>
                <p className="text-sm text-white/60 mt-1">Restricted access. Actions here affect production data.</p>

                <form onSubmit={submit} className="mt-6 space-y-3">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/60">Email</label>
                        <input
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="admin-email-input"
                            className="mt-1 h-12 w-full rounded-xl bg-white/5 border border-white/10 px-4 text-sm outline-none focus:border-[#E85D5D]"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/60">Password</label>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="admin-password-input"
                            className="mt-1 h-12 w-full rounded-xl bg-white/5 border border-white/10 px-4 text-sm outline-none focus:border-[#E85D5D]"
                        />
                    </div>
                    {error && (
                        <div className="text-xs text-[#F56565] font-semibold" data-testid="admin-login-error">
                            {error}
                        </div>
                    )}
                    <Button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="w-full h-12 rounded-xl bg-[#E85D5D] hover:bg-[#E85D5D]/90 text-white font-black lift"
                    >
                        <Lock className="mr-2 h-4 w-4" /> {loading ? "Signing in…" : "Enter console"}
                    </Button>
                </form>

                <p className="text-[10px] text-white/40 mt-6 text-center">
                    All access is audited. Unauthorised entry attempts are logged.
                </p>
            </motion.div>
        </div>
    );
}
